import React, {ReactNode, useMemo} from "react";
import {Loading} from "./LoadingData";
import type {StoreApi} from "zustand/vanilla";
import {RemoteValue} from "../../util/RemoteValue";
import {useStore} from "zustand/react";
import {useDefaultKeyVaultStore} from "../key-vault-default/KeyVaultDefault";
import {KeyVault, Neighbourhood, Office} from "../../api/Model";
import {useKeyVaultStore} from "../../api/KeyVaultStore";
import {useNeighbourhoodStore} from "../../api/NeighbourhoodStore";
import {useOfficeStore} from "../../api/OfficeStore";
import {ResidenceModel, useResidenceStore} from "../../api/ResidenceStore";
import {usePermission} from "../../util/access/UseAccess";
import {KeyVaults_Read, Neighbourhoods_Read, Offices_Read, WorkSites_Read} from "../../util/access/Permission";

export function LoadStore<V>({children, storeApi}: {
    storeApi: StoreApi<{ remoteValue: RemoteValue<V> }>,
    children: (resolvedValue: V) => ReactNode
}) {
    const remoteValue: RemoteValue<V> = useStore(storeApi, state => state.remoteValue);
    return (
        <Loading
            loading={<div>Loading...</div>}
            value={remoteValue}
            children={children}
        />
    );
}

export function DefaultKeyVaultProvider({children}: {
    children: (keyVault: KeyVault) => React.ReactNode,
}) {
    return (
        <LoadStore storeApi={useDefaultKeyVaultStore} children={children}/>
    );
}

export function FilterStoreProvider({children}: {
    children: (filterStores: {
        defaultKeyVault: KeyVault,
        keyVaults: KeyVault[],
        residenceModel: ResidenceModel,
        offices: Office[],
        neighbourhoods: Neighbourhood[],
    }) => ReactNode,
}) {
    const {canReadVaults, canReadWorkSites, canReadOffices, canReadNeighbourhoods} = usePermission({
        canReadVaults: KeyVaults_Read,
        canReadWorkSites: WorkSites_Read,
        canReadNeighbourhoods: Neighbourhoods_Read,
        canReadOffices: Offices_Read,
    });
    const defaultKeyVault = useDefaultKeyVaultStore(state => state.remoteValue);
    const keyVaults = useKeyVaultStore(state => state.remoteValue);
    const residencyModel = useResidenceStore().remoteValue;
    const neighbourhoods = useNeighbourhoodStore(state => state.remoteValue);
    const offices = useOfficeStore(state => state.remoteValue);

    const filterStores = useMemo(() => {
        return RemoteValue.combine({
            defaultKeyVault,
            keyVaults: canReadVaults ? keyVaults : RemoteValue.fromValue([]),
            residenceModel: canReadWorkSites ? residencyModel : RemoteValue.fromValue(ResidenceModel.EMPTY),
            offices: canReadOffices ? offices : RemoteValue.fromValue([]),
            neighbourhoods: canReadNeighbourhoods ? neighbourhoods : RemoteValue.fromValue([])
        });
    }, [defaultKeyVault, keyVaults, residencyModel, offices, neighbourhoods, canReadOffices, canReadNeighbourhoods, canReadVaults, canReadWorkSites]);

    return (
        <Loading
            loading={<div>Loading...</div>}
            value={filterStores}
            children={children}
        />
    );
}

